<template>
  <b-card no-body>
    <!-- Loading Spinner -->
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <!-- Error Alert -->
    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>{{ loadingError }}</p>
      </div>
    </b-alert>

    <!-- Action Buttons -->
    <div
      v-if="!loading && !loadingError"
      class="text-right mb-2"
    >
      <b-button
        variant="outline-primary"
        class="mr-1 refresh-btn"
        :class="isNewItemAvailable ? 'pr-3' : ''"
        :disabled="loading"
        @click="handleRefresh"
      >
        Refresh
        <span
          v-if="isNewItemAvailable"
          class="badge badge-up badge-pill badge-success badge-minimal badge-glow"
        />
      </b-button>
      <b-button
        variant="outline-primary"
        :disabled="!selectedBuyers.length"
        @click="handleSetTarget"
      >
        Set Target
      </b-button>
    </div>

    <!-- Table Container with Horizontal Scroll -->
    <div
      v-if="!loading && !loadingError"
      class="table-responsive-wrapper"
    >
      <b-table
        :items="results"
        :fields="dynamicTableFields"
        responsive="sm"
        striped
        hover
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        class="compact-table"
        no-local-sorting
        :tbody-tr-class="applyRowClass"
        @sort-changed="sortingChanged"
        @row-clicked="handleRowClick"
      >
        <!-- Select Checkbox Column -->
        <template #head(select)>
          <b-form-checkbox
            v-model="isAllSelected"
            :disabled="results.length === 0"
            @change="handleAllSelectChange"
          />
        </template>

        <template #cell(select)="data">
          <b-form-checkbox
            v-model="selectedBuyers"
            :value="data.item.buyer_id"
            name="select-buyer"
          />
        </template>

        <!-- Buyer Column -->
        <template #cell(buyer)="data">
          <div>
            <strong class="text-primary">{{ data.item.buyer_identifier }}</strong>
          </div>
        </template>

        <!-- Area Column -->
        <template #cell(area)="data">
          {{ data.item.area_name || '-' }}
        </template>

        <!-- Salesman Column -->
        <template #cell(salesman)="data">
          {{ data.item.salesman_name || '-' }}
        </template>

        <!-- Dynamic Monthly Columns -->
        <template
          v-for="monthKey in monthKeys"
          #[`cell(${monthKey})`]="data"
        >
          <div
            :key="monthKey"
            class="text-right"
          >
            <div class="small">
              <strong>{{ formatCurrency(getMonthData(data.item, monthKey).net_sales) }}</strong>
            </div>
            <div
              class="text-success"
              style="font-size: 0.75rem;"
            >
              S: {{ formatCurrency(getMonthData(data.item, monthKey).total_sales) }}
            </div>
            <div
              class="text-danger"
              style="font-size: 0.75rem;"
            >
              R: {{ formatCurrency(getMonthData(data.item, monthKey).total_returns) }}
            </div>
            <div
              class="text-primary"
              style="font-size: 0.75rem;"
            >
              T: {{ formatCurrency(getMonthData(data.item, monthKey).set_target) }}
            </div>
          </div>
        </template>

        <!-- Grand Total Sales -->
        <template #cell(grand_total_sales)="data">
          <div class="text-right">
            <strong>{{ formatCurrency(data.item.grand_total_sales) }}</strong>
          </div>
        </template>

        <!-- Grand Total Returns -->
        <template #cell(grand_total_returns)="data">
          <div class="text-right text-danger">
            {{ formatCurrency(data.item.grand_total_returns) }}
          </div>
        </template>

        <!-- Grand Net Sales -->
        <template #cell(grand_net_sales)="data">
          <div class="text-right">
            <strong>{{ formatCurrency(data.item.grand_net_sales) }}</strong>
          </div>
        </template>

        <!-- Target Column with Input and Month Selector -->
        <template #head(target)>
          <div class="d-flex flex-column">
            <span class="mb-1">Target</span>
            <flat-pickr
              id="targetMonth"
              v-model="targetMonth"
              placeholder="Select Month"
              :config="flatpickrConfig"
              class="form-control form-control-sm"
              style="font-size: 0.875rem;"
            />
          </div>
        </template>

        <template #cell(target)="data">
          <div class="d-flex align-items-center">
            <b-form-input
              v-model.number="data.item.target"
              type="number"
              size="sm"
              class="text-right target-input"
              :class="getTargetInputClass(data.item)"
              style="width: 120px;margin-right: 4px;"
            />
            <feather-icon
              v-b-tooltip.hover.top="'Copy last target'"
              icon="CopyIcon"
              size="16"
              class="cursor-pointer text-primary"
              @click="copyLastTarget(data.item)"
            />
          </div>
        </template>
      </b-table>
    </div>

    <!-- Pagination -->
    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => { currentPage = page }"
    />

    <!-- Set Target Modal -->
    <b-modal
      v-model="showSetTargetModal"
      title="Set Target"
      ok-title="Confirm"
      cancel-title="Cancel"
      @ok="confirmSetTarget"
    >
      <p>
        Are you sure you want to set targets for <strong>{{ selectedBuyers.length }}</strong> buyer(s) for
        <strong>{{ formatMonthDisplay(targetMonth) }}</strong>?
      </p>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BSpinner,
  BTable,
  BAlert,
  BFormInput,
  BFormCheckbox,
  BButton,
  BModal,
  VBTooltip,
  BCard,
} from 'bootstrap-vue'
import axios from 'axios'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index'
import 'flatpickr/dist/plugins/monthSelect/style.css'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import dateMixin from '@/mixins/dateMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BAlert,
    BFormInput,
    BFormCheckbox,
    BButton,
    BModal,
    BCard,
    DetailedPagination,
    flatPickr,
  },
  mixins: [dateMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      currentPage: 1,
      perPage: 50,
      sortBy: 'buyer_identifier',
      results: [],
      totalItems: 0,
      sortDesc: false,
      monthKeys: [],
      selectedBuyers: [],
      isAllSelected: false,
      isNewItemAvailable: false,
      targetMonth: '',
      showSetTargetModal: false,
      flatpickrConfig: {
        dateFormat: 'Y-m',
        altInput: true,
        altFormat: 'F Y',
        plugins: [new MonthSelectPlugin({ shorthand: true, dateFormat: 'Y-m', altFormat: 'F Y' })],
      },
    }
  },
  computed: {
    dynamicTableFields() {
      const fields = [
        {
          key: 'select',
          label: '',
          thStyle: { width: '50px' },
          stickyColumn: true,
        },
        {
          key: 'buyer',
          label: 'Buyer',
          sortable: true,
          thStyle: { width: '250px', minWidth: '250px' },
          stickyColumn: true,
        },
        {
          key: 'area',
          label: 'Area',
          sortable: true,
          thStyle: { width: '120px' },
        },
        {
          key: 'salesman',
          label: 'Salesman',
          sortable: true,
          thStyle: { width: '120px' },
        },
      ]

      // Add dynamic month columns
      this.monthKeys.forEach(monthKey => {
        fields.push({
          key: monthKey,
          label: this.formatMonthLabel(monthKey),
          sortable: false,
          thStyle: { width: '120px', textAlign: 'right' },
          tdClass: 'text-right',
        })
      })

      // Add summary columns
      fields.push(
        {
          key: 'grand_total_sales',
          label: 'Total Sales',
          sortable: true,
          thStyle: { width: '120px', textAlign: 'right' },
          tdClass: 'text-right',
        },
        {
          key: 'grand_total_returns',
          label: 'Total Returns',
          sortable: true,
          thStyle: { width: '120px', textAlign: 'right' },
          tdClass: 'text-right',
        },
        {
          key: 'grand_net_sales',
          label: 'Net Sales',
          sortable: true,
          thStyle: { width: '130px', textAlign: 'right' },
          tdClass: 'text-right',
        },
        {
          key: 'target',
          label: 'Target',
          sortable: false,
          thStyle: { width: '170px' },
        },
      )

      return fields
    },
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters: {
      deep: true,
      handler() {
        if (this.currentPage !== 1) {
          this.currentPage = 1
        } else {
          this.fetchItems()
        }
      },
    },
    selectedBuyers(newVal) {
      if (this.results.length > 0 && newVal.length === this.results.length) {
        this.isAllSelected = true
      } else {
        this.isAllSelected = false
      }
    },
  },
  created() {
    this.setDefaultTargetMonth()
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      try {
        this.loading = true
        this.loadingError = null

        const preparedPayload = {
          end_month: this.filters.end_month,
          start_month: this.filters.start_month,
          buyer_ids: this.filters.buyer,
          salesman_ids: this.filters.salesmans,
          area_ids: this.filters.area,
          mode_of_payment: this.filters.buyer_type,
          sort_by: this.filters.sort_by,
          sort_order: this.filters.sort_order,
          has_current_month_target: this.filters.has_current_month_target,
          page: this.currentPage,
          page_size: this.perPage,
        }
        const response = await axios.get(
          `/accounts/buyer_monthly_sales?${getParsedQueryString(preparedPayload)}`,
        )

        this.results = response.data.data.map(item => ({
          ...item,
          target: item.set_target || item.ref_target || null,
        }))
        this.totalItems = response.data.meta?.total_count || response.data.data.length

        // Extract unique months from the data
        this.extractMonthKeys()
      } catch (error) {
        this.loadingError = error?.response?.data?.details || 'Failed to fetch monthly sales data.'
      } finally {
        this.loading = false
        this.isNewItemAvailable = false
      }
    },

    extractMonthKeys() {
      // Get all unique months from the first result's monthly_data
      if (this.results.length > 0 && this.results[0].monthly_data) {
        this.monthKeys = this.results[0].monthly_data.map(item => item.month)
      } else {
        this.monthKeys = []
      }
    },

    getMonthData(item, monthKey) {
      const monthData = item.monthly_data?.find(m => m.month === monthKey)
      return monthData || {
        total_sales: 0, total_returns: 0, net_sales: 0, set_target: 0,
      }
    },

    formatMonthLabel(monthKey) {
      // Convert "2025-07" to "Jul 2025"
      const [year, month] = monthKey.split('-')
      const date = new Date(year, parseInt(month, 10) - 1, 1)
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    },

    formatMonthDisplay(monthKey) {
      if (!monthKey) return ''
      const [year, month] = monthKey.split('-')
      const date = new Date(year, parseInt(month, 10) - 1, 1)
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    },

    formatCurrency(value) {
      if (!value || value === 0) return '0'
      return new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 0,
      }).format(value)
    },

    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    setDefaultTargetMonth() {
      const now = new Date()
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
      const year = nextMonth.getFullYear()
      const month = nextMonth.getMonth() + 1
      this.targetMonth = `${year}-${String(month).padStart(2, '0')}`
    },

    handleRefresh() {
      this.fetchItems()
    },

    handleAllSelectChange(isChecked) {
      this.selectedBuyers = isChecked
        ? this.results.map(data => data.buyer_id)
        : []
    },

    handleRowClick(item, index, event) {
      // Prevent row click when clicking on links or other interactive elements
      if (event.target.tagName === 'A' || event.target.closest('a') || event.target.closest('svg')
    || event.target.tagName === 'svg' || event.target.closest('input')) {
        return
      }
      const itemId = item.buyer_id
      const isSelected = this.selectedBuyers.includes(itemId)

      if (isSelected) {
        // Remove from selection
        this.selectedBuyers = this.selectedBuyers.filter(id => id !== itemId)
      } else {
        // Add to selection
        this.selectedBuyers = [...this.selectedBuyers, itemId]
      }
    },

    applyRowClass(item) {
      return this.selectedBuyers.includes(item?.buyer_id) ? 'selected-row' : ''
    },

    getTargetInputClass(item) {
      if (item.set_target) {
        return 'border-success'
      }
      if (item.ref_target) {
        return 'border-warning'
      }
      return ''
    },

    handleSetTarget() {
      if (!this.targetMonth) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please select a target month',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      const hasInvalidTargets = this.selectedBuyers.some(buyerId => {
        const buyer = this.results.find(r => r.buyer_id === buyerId)
        return !buyer.target || buyer.target <= 0
      })

      if (hasInvalidTargets) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please enter valid target amounts for all selected buyers',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      this.showSetTargetModal = true
    },

    async confirmSetTarget() {
      try {
        const [year, month] = this.targetMonth.split('-')
        const monthYear = `${year}-${month}-1`

        const targets = this.selectedBuyers.map(buyerId => {
          const buyer = this.results.find(r => r.buyer_id === buyerId)
          return {
            buyer: buyerId,
            target_amount: buyer.target,
          }
        })

        const payload = {
          month_year: monthYear,
          targets,
        }

        await axios.post('/accounts/buyer_monthly_target_bulk/', payload)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `Targets set successfully for ${targets.length} buyer(s)`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Clear selection and refresh data
        this.selectedBuyers = []
        await this.fetchItems()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error?.response?.data?.details || 'Failed to set targets',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
    copyLastTarget(item) {
    // Get the last available target from monthly_data
      if (!item.monthly_data || item.monthly_data.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No Target Found',
            text: 'No previous target found in monthly data',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Iterate through monthly_data in reverse order to find the last set target
      let lastTarget = null
      for (let i = item.monthly_data.length - 1; i >= 0; i -= 1) {
        const monthData = item.monthly_data[i]
        if (monthData.set_target && monthData.set_target > 0) {
          lastTarget = monthData.set_target
          break
        }
      }

      if (lastTarget) {
      // Update the target field with the last available target
        this.$set(item, 'target', lastTarget)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Target Copied',
            text: `Last target (${this.formatCurrency(lastTarget)}) copied successfully`,
            icon: 'CopyIcon',
            variant: 'success',
          },
        })
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No Target Found',
            text: 'No previous target found in monthly data',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
/* Horizontal scroll wrapper */
.table-responsive-wrapper {
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
}

.compact-table >>> td {
  padding: 0.5rem;
  vertical-align: middle;
}

.compact-table >>> th {
  padding: 0.5rem;
  white-space: nowrap;
}

/* Make buyer column and checkbox sticky */
.compact-table >>> th:first-child,
.compact-table >>> td:first-child,
.compact-table >>> th:nth-child(2),
.compact-table >>> td:nth-child(2) {
  position: sticky;
  background-color: white;
  z-index: 2;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

.compact-table >>> th:first-child,
.compact-table >>> td:first-child {
  left: 0;
}

.compact-table >>> th:nth-child(2),
.compact-table >>> td:nth-child(2) {
  left: 50px;
}

.compact-table >>> thead th:first-child,
.compact-table >>> thead th:nth-child(2) {
  z-index: 3;
}

.compact-table >>> tbody tr:hover td:first-child,
.compact-table >>> tbody tr:hover td:nth-child(2) {
  background-color: #f8f9fa;
}

/* Ensure table has min-width for scrolling */
.compact-table {
  min-width: 100%;
  margin-bottom: 0;
}

/* Target input styling */
.target-input {
  border-width: 2px !important;
}

.target-input.border-success {
  border-color: #28a745 !important;
}

.target-input.border-warning {
  border-color: #ffc107 !important;
}

/* Remove number input arrows */
.target-input::-webkit-outer-spin-button,
.target-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Selected row styling */
.selected-row {
  background-color: #f0f8ff !important;
}

/* Refresh button with badge */
.refresh-btn {
  position: relative;
}

.refresh-btn .badge {
  height: 9px;
  width: 9px;
  min-width: 1px;
  min-height: 1px;
  right: 17px;
  top: 14px;
}
</style>
